import type { ThinkingStep } from '@/components/ui/thinking-state-clean';
import type { ChatMessage, LibrarySearch } from '@/types/chat';

import { libraryDocumentHref } from './libraryDocuments';

/**
 * The working panel's timeline for a live Connie turn (VH3_CONNECT #549): the
 * closed progress notes in order (Connie's narration and thinking between tools),
 * with each Library search placed where it happened and its documents as source
 * chips. The open status line, if any, is the active last step.
 */

const VISIBLE_SOURCES = 4;

const searchStep = (search: LibrarySearch): ThinkingStep => {
  const sources = search.documents.map(document => ({
    label: document.name || 'Library Document',
    href: libraryDocumentHref(document.documentId),
    tooltip: 'Open in the Library',
  }));
  const count = search.documents.length;
  return {
    kind: 'search',
    label: count
      ? `Searched the Library · ${count} ${count === 1 ? 'document' : 'documents'}`
      : 'Searched the Library',
    activeLabel: 'Searching the Library',
    sources: sources.slice(0, VISIBLE_SOURCES),
    moreSources:
      sources.length > VISIBLE_SOURCES
        ? sources.slice(VISIBLE_SOURCES)
        : undefined,
  };
};

export function buildWorkingSteps(
  message: Pick<
    ChatMessage,
    'progressNotes' | 'progressStatus' | 'librarySearches'
  >
): ThinkingStep[] {
  const notes = message.progressNotes ?? [];
  const searches = message.librarySearches ?? [];
  const steps: ThinkingStep[] = [];

  const searchesAfter = (index: number) =>
    searches.filter(search => search.afterNotes === index);

  notes.forEach((note, index) => {
    for (const search of searchesAfter(index)) steps.push(searchStep(search));
    steps.push({ kind: 'step', label: note });
  });
  for (const search of searches) {
    if (search.afterNotes >= notes.length) steps.push(searchStep(search));
  }

  const status = message.progressStatus?.trim();
  if (status && !notes.includes(status)) {
    steps.push({ kind: 'step', label: status });
  }
  return steps;
}

/** When the turn started and, once every step has ended, how long it took. */
export function workingTiming(message: Pick<ChatMessage, 'thinkingSteps'>): {
  startedAt?: number;
  durationMs?: number;
} {
  const steps = message.thinkingSteps ?? [];
  if (!steps.length) return {};
  const startedAt = Math.min(...steps.map(step => step.startedAt));
  const ends = steps.map(step => step.endedAt);
  if (ends.some(end => end == null)) return { startedAt };
  const endedAt = Math.max(...(ends as number[]));
  return { startedAt, durationMs: Math.max(0, endedAt - startedAt) };
}
